import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { isAppError } from '@/shared/api/errors';
import { queryKeys } from '@/shared/api/queryKeys';
import { theme } from '@/theme';

/** Any 401 means the session ended (expired or signed out elsewhere): RequireAuth then shows the login page. */
const signOutOn401 = (error: unknown) => {
  if (isAppError(error) && error.status === 401) queryClient.setQueryData(queryKeys.session, null);
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: signOutOn401 }),
  mutationCache: new MutationCache({ onError: signOutOn401 }),
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: (count, error) => {
        const status = isAppError(error) ? error.status : undefined;
        return status !== undefined && status < 500 ? false : count < 2;
      },
    },
  },
});

export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </ThemeProvider>
  );
}
